import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function NovaAvatar({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" className="nova-avatar" aria-hidden="true">
      <defs>
        <linearGradient id="novaGrad" x1="0" y1="0" x2="40" y2="40">
          <stop offset="0%" stopColor="#8b7cf6" />
          <stop offset="100%" stopColor="#5ad8d8" />
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="19" fill="url(#novaGrad)" opacity="0.15" />
      <rect x="10" y="14" width="20" height="16" rx="7" fill="url(#novaGrad)" />
      <circle cx="20" cy="9" r="2.4" fill="url(#novaGrad)" />
      <line x1="20" y1="11.2" x2="20" y2="14" stroke="url(#novaGrad)" strokeWidth="1.6" />
      <circle cx="15.5" cy="21.5" r="2.6" fill="#0f1024" />
      <circle cx="24.5" cy="21.5" r="2.6" fill="#0f1024" />
      <path d="M16 26.5C17.2 27.7 22.8 27.7 24 26.5" stroke="#0f1024" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    </svg>
  );
}

// ── WebAuthn helpers ──────────────────────────────────────────────────────────

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  return Uint8Array.from(binary, c => c.charCodeAt(0));
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function webauthnCall(action, extra = {}) {
  const res = await fetch('/.netlify/functions/webauthn', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...extra }),
  });
  return res.json();
}

async function registerPasskey() {
  const options = await webauthnCall('register-start');
  if (options.error) throw new Error(options.error);

  options.challenge = base64urlToBuffer(options.challenge);
  options.user.id = base64urlToBuffer(options.user.id);
  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map(c => ({
      ...c, id: base64urlToBuffer(c.id),
    }));
  }

  const credential = await navigator.credentials.create({ publicKey: options });
  const credJson = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(credential.response.attestationObject),
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
    },
  };

  const result = await webauthnCall('register-finish', { credential: credJson });
  if (!result.success) throw new Error(result.error || 'Registration failed');
  return true;
}

async function authenticatePasskey() {
  const options = await webauthnCall('auth-start');
  if (options.error) throw new Error(options.error);
  if (options.needsSetup) return { needsSetup: true };

  options.challenge = base64urlToBuffer(options.challenge);
  if (options.allowCredentials) {
    options.allowCredentials = options.allowCredentials.map(c => ({
      ...c, id: base64urlToBuffer(c.id),
    }));
  }

  const assertion = await navigator.credentials.get({ publicKey: options });
  const assertJson = {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      signature: bufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle ? bufferToBase64url(assertion.response.userHandle) : null,
    },
  };

  const result = await webauthnCall('auth-finish', { credential: assertJson });
  if (!result.success) throw new Error(result.error || 'Authentication failed');
  return { success: true };
}

// ── Login screen ──────────────────────────────────────────────────────────────

function LoginScreen({ onAuthenticated }) {
  const [status, setStatus] = useState('idle'); // idle | loading | error | registering
  const [errorMsg, setErrorMsg] = useState('');
  const [showRegister, setShowRegister] = useState(false);

  const handleLogin = async () => {
    setStatus('loading');
    setErrorMsg('');
    try {
      const result = await authenticatePasskey();
      if (result.needsSetup) {
        setShowRegister(true);
        setStatus('idle');
      } else {
        onAuthenticated();
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setErrorMsg('Face ID was cancelled or not recognised. Try again.');
      } else {
        setErrorMsg(err.message);
      }
      setStatus('error');
    }
  };

  const handleRegister = async () => {
    setStatus('registering');
    setErrorMsg('');
    try {
      await registerPasskey();
      onAuthenticated();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setErrorMsg('Registration was cancelled. Try again.');
      } else {
        setErrorMsg(err.message);
      }
      setStatus('error');
    }
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <NovaAvatar size={56} />
        <h1 className="login-title">Nova</h1>
        <p className="login-subtitle">Your personal AI assistant</p>

        {!showRegister ? (
          <>
            <button
              className="login-btn"
              onClick={handleLogin}
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <>
                  <span className="login-spinner" />
                  Verifying…
                </>
              ) : (
                <>
                  <FaceIdIcon />
                  Login with Face ID
                </>
              )}
            </button>
            <p className="login-hint">First time? <button className="login-link" onClick={() => setShowRegister(true)}>Set up Face ID</button></p>
          </>
        ) : (
          <>
            <p className="login-register-text">Register your face or fingerprint to secure Nova. You only need to do this once per device.</p>
            <button
              className="login-btn"
              onClick={handleRegister}
              disabled={status === 'registering'}
            >
              {status === 'registering' ? (
                <>
                  <span className="login-spinner" />
                  Registering…
                </>
              ) : (
                <>
                  <FaceIdIcon />
                  Register Face ID
                </>
              )}
            </button>
            <p className="login-hint"><button className="login-link" onClick={() => setShowRegister(false)}>Back to login</button></p>
          </>
        )}

        {(status === 'error') && (
          <p className="login-error">{errorMsg}</p>
        )}
      </div>
    </div>
  );
}

function FaceIdIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 2H7a2 2 0 0 0-2 2v2" />
      <path d="M15 2h2a2 2 0 0 1 2 2v2" />
      <path d="M9 22H7a2 2 0 0 1-2-2v-2" />
      <path d="M15 22h2a2 2 0 0 0 2-2v-2" />
      <circle cx="9" cy="10" r="1" fill="currentColor" />
      <circle cx="15" cy="10" r="1" fill="currentColor" />
      <path d="M9 15s1 1.5 3 1.5 3-1.5 3-1.5" />
      <line x1="12" y1="7" x2="12" y2="9" />
    </svg>
  );
}

// ── Memory helpers ────────────────────────────────────────────────────────────

async function loadMemory() {
  try {
    const res = await fetch('/.netlify/functions/memory');
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

async function saveConversationTurns(turns) {
  try {
    await fetch('/.netlify/functions/memory', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turns }),
    });
  } catch (e) { console.warn('Failed to save conversation turns:', e); }
}

function formatHistoryContext(history) {
  if (!history || history.length === 0) return null;
  const lines = history.map(h => `${h.role === 'user' ? 'Chris' : 'Nova'}: ${h.content}`).join('\n');
  return `<recent_conversation_history>\nThe following are recent exchanges between Chris and Nova from previous sessions:\n${lines}\n</recent_conversation_history>`;
}

async function loadNotes() {
  try {
    const res = await fetch('/.netlify/functions/notes');
    if (!res.ok) return [];
    const data = await res.json();
    return data.notes || [];
  } catch { return []; }
}

async function addNote(text, due) {
  try {
    await fetch('/.netlify/functions/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'add', text, due: due || null }),
    });
  } catch (e) { console.warn('Note add failed:', e); }
}

async function removeNote(text) {
  try {
    await fetch('/.netlify/functions/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'remove', text }),
    });
  } catch (e) { console.warn('Note remove failed:', e); }
}

function formatNotesContext(notes) {
  if (!notes || notes.length === 0) return null;
  const lines = notes.map(n => {
    const due = n.due ? ` (due: ${n.due})` : '';
    return `  - ${n.text}${due}`;
  });
  return `<notes>
Active reminders and notes:
${lines.join('\n')}
</notes>`;
}

function parseNoteAction(text) {
  const addMatch = text.match(/\[NOTE_ADD\]([\s\S]*?)\[\/NOTE_ADD\]/);
  const removeMatch = text.match(/\[NOTE_REMOVE\]([\s\S]*?)\[\/NOTE_REMOVE\]/);
  if (addMatch) {
    try { return { action: 'add', data: JSON.parse(addMatch[1].trim()), raw: addMatch[0] }; } catch {}
  }
  if (removeMatch) {
    try { return { action: 'remove', data: JSON.parse(removeMatch[1].trim()), raw: removeMatch[0] }; } catch {}
  }
  return null;
}

async function saveMemory(update) {
  try {
    await fetch('/.netlify/functions/memory', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    });
  } catch (e) { console.warn('Memory save failed:', e); }
}

function formatMemoryContext(memory) {
  if (!memory) return null;
  const lines = [];
  if (memory.preferences && Object.keys(memory.preferences).length > 0) {
    lines.push('Preferences:\n' + Object.entries(memory.preferences).map(([k, v]) => `  ${k}: ${v}`).join('\n'));
  }
  if (memory.memories && memory.memories.length > 0) {
    lines.push('Things to remember:\n' + memory.memories.map(m => `  - ${m}`).join('\n'));
  }
  if (memory.conversation_summary) {
    lines.push(`Recent context: ${memory.conversation_summary}`);
  }
  if (lines.length === 0) return null;
  return `<memory>\n${lines.join('\n\n')}\n</memory>`;
}

function parseMemoryUpdate(text) {
  const match = text.match(/\[MEMORY_UPDATE\]([\s\S]*?)\[\/MEMORY_UPDATE\]/);
  if (!match) return { cleanText: text, memoryUpdate: null };
  const cleanText = (text.slice(0, match.index) + text.slice(match.index + match[0].length)).trim();
  try {
    const memoryUpdate = JSON.parse(match[1].trim());
    return { cleanText, memoryUpdate };
  } catch {
    return { cleanText, memoryUpdate: null };
  }
}

// ── Detection helpers ─────────────────────────────────────────────────────────

function mentionsEmail(text) {
  const t = text.toLowerCase();
  // Must mention email in context of checking/reading own emails, not just the word "email"
  const emailKeywords = ['inbox', 'mailbox', 'check my email', 'my emails', 'unread', 'my mail'];
  if (emailKeywords.some(kw => t.includes(kw))) return true;
  // "email" alone only triggers if combined with inbox/read/check/folder context
  if (t.includes('email') || t.includes('mail')) {
    return ['check', 'read', 'inbox', 'folder', 'unread', 'show', 'search my', 'find my'].some(kw => t.includes(kw));
  }
  return false;
}

function mentionsWeather(text) {
  const t = text.toLowerCase();
  return ['weather', 'temperature', 'forecast', 'rain', 'sunny', 'cold', 'hot', 'wind', 'humid'].some((kw) => t.includes(kw));
}

function mentionsContacts(text, messages) {
  const t = text.toLowerCase();
  // Direct contact lookup
  const directTrigger = ["what's", "whats", "contact", "phone", "number", "email address", "address"].some(kw => t.includes(kw)) &&
    ["contact", "phone", "number", "email address", "address", "details", "reach"].some(kw => t.includes(kw));
  if (directTrigger) return true;

  // Follow-up patterns like "what about X?" or "and X?" after a contacts conversation
  const isFollowUp = /^(what about|and|how about|what's|whats)\s+[A-Z]/i.test(text.trim());
  if (isFollowUp && messages && messages.length > 0) {
    // Check if recent messages contained contact info
    const recentAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    if (recentAssistant) {
      const content = typeof recentAssistant.content === 'string' ? recentAssistant.content : '';
      if (content.includes('contacts') || content.includes('phone') || content.includes('number') || content.includes('email')) {
        return true;
      }
    }
  }
  return false;
}

async function fetchContactsContext(userText) {
  // Extract the name being searched for
  
  // Extract capitalised words, filtering out common question words
  const stopWords = new Set(['What', 'Who', 'Where', 'When', 'How', 'Can', 'Does', 'Is', 'Are', 'The', 'Do', 'Get', 'Find', 'Look', 'Show', 'Tell', 'Give', 'Please', 'About', 'And']);
  // Strip possessives before extracting names
  const cleanText = userText.replace(/'s\b/gi, '').replace(/\u2019s\b/gi, '');
  const words = (cleanText.match(/[A-Z][a-z]+/g) || []).filter(w => !stopWords.has(w));
  const searchTerm = words.length ? words.join(' ') : cleanText.replace(/[^a-zA-Z ]/g, '').trim();

  if (!searchTerm || searchTerm.length < 2) return null;

  try {
    const res = await fetch('/.netlify/functions/contacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ search: searchTerm }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const contacts = data.contacts || [];
    if (contacts.length === 0) return `<contacts>No contacts found matching "${searchTerm}".</contacts>`;
    const lines = contacts.map(c => {
      const parts = [`Name: ${c.name}`];
      if (c.emails?.length) parts.push(`Email: ${c.emails.join(', ')}`);
      if (c.phones?.length) parts.push(`Phone: ${c.phones.join(', ')}`);
      if (c.organisation) parts.push(`Organisation: ${c.organisation}`);
      return parts.join(' | ');
    });
    return `<contacts>\n${lines.join('\n')}\n</contacts>`;
  } catch (err) {
    return `<contacts>Failed to fetch contacts: ${err.message}</contacts>`;
  }
}

function mentionsCalendar(text) {
  const t = text.toLowerCase();
  return ['calendar', 'schedule', 'diary', 'appointment', 'meeting', 'event', 'birthday', 'anniversary', 'reminder', 'what have i got', 'what do i have', "what's on", 'busy', 'free', 'week', 'today', 'tomorrow', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'search for', 'when is', 'find'].some((kw) => t.includes(kw));
}

async function fetchCalendarContext(userText) {
  const t = userText.toLowerCase();
  let daysAhead = 14; // default to 2 weeks

  // Specific event searches get a long window
  const isSpecificSearch = ['birthday', 'anniversary', 'search for', 'when is', 'find', 'look for'].some(kw => t.includes(kw));
  if (isSpecificSearch) daysAhead = 365;
  else if (t.includes('today')) daysAhead = 1;
  else if (t.includes('tomorrow')) daysAhead = 2;
  else if (t.match(/\d+\s*days?/)) {
    const match = t.match(/(\d+)\s*days?/);
    daysAhead = Math.min(parseInt(match[1]), 365);
  }
  else if (t.includes('week')) daysAhead = 7;
  else if (t.includes('fortnight') || t.includes('two week')) daysAhead = 14;
  else if (t.includes('month')) daysAhead = 30;
  else if (t.includes('year')) daysAhead = 365;

  try {
    const res = await fetch('/.netlify/functions/calendar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ daysAhead }),
    });
    if (!res.ok) {
      const err = await res.json();
      return `<calendar>Failed to fetch calendar: ${err.error}</calendar>`;
    }
    const data = await res.json();
    const events = data.events || [];
    if (events.length === 0) {
      return `<calendar>No events found in the next ${daysAhead} day(s).</calendar>`;
    }
    const lines = events.map(e => {
      const start = new Date(e.start);
      const dateStr = e.allDay
        ? start.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
        : start.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
      const loc = e.location ? ` — ${e.location}` : '';
      const cal = e.calendar ? ` [${e.calendar}]` : '';
      return `${dateStr}: ${e.summary}${loc}${cal}`;
    });
    return `<calendar>\n${lines.join('\n')}\n</calendar>`;
  } catch (err) {
    return `<calendar>Failed to fetch calendar: ${err.message}</calendar>`;
  }
}

function extractLocationFromText(text) {
  const match = text.match(/(?:in|for|at)\s+([A-Z][a-zA-Z\s]{2,30})(?:[?,.]|$)/);
  return match ? match[1].trim() : null;
}

async function getBrowserLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 6000 }
    );
  });
}

async function fetchWeatherContext(userText) {
  const placeName = extractLocationFromText(userText);
  let body;

  if (placeName) {
    body = { location: placeName };
  } else {
    const coords = await getBrowserLocation();
    if (coords) {
      body = { lat: coords.lat, lon: coords.lon };
    } else {
      return '<weather>Could not determine location — browser denied GPS access and no place name was found in the message.</weather>';
    }
  }

  try {
    const res = await fetch('/.netlify/functions/weather', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json();
      return `<weather>Failed to fetch weather: ${err.error}</weather>`;
    }
    const data = await res.json();
    const c = data.current;
    const dailyLines = data.daily.map(d =>
      `  ${d.date}: ${d.description}, High ${d.max}${c.unit} / Low ${d.min}${c.unit}, Precip ${d.precipitation}mm`
    ).join('\n');
    return `<weather>
Location: ${data.location}
Current: ${c.description}, ${c.temp}${c.unit} (feels like ${c.feels_like}${c.unit}), Humidity ${c.humidity}%, Wind ${c.wind_speed} km/h
3-day forecast:
${dailyLines}
</weather>`;
  } catch (err) {
    return `<weather>Failed to fetch weather: ${err.message}</weather>`;
  }
}

function formatEmailsForPrompt(emails) {
  if (!emails.length) return '<emails>No emails found.</emails>';
  const blocks = emails.map((e, i) =>
    `[Email ${i + 1}]\nFrom: ${e.from}\nSubject: ${e.subject}\nDate: ${e.date}\nBody:\n${e.bodyPreview}`
  );
  return `<emails>\n${blocks.join('\n\n---\n\n')}\n</emails>`;
}

function parseEmailDraft(text) {
  const match = text.match(/\[EMAIL_DRAFT\]([\s\S]*?)\[\/EMAIL_DRAFT\]/);
  if (!match) return { before: text, draft: null, after: '' };

  const before = text.slice(0, match.index).trim();
  const after = text.slice(match.index + match[0].length).trim();
  const block = match[1];

  const toMatch = block.match(/TO:\s*(.*)/);
  const subjectMatch = block.match(/SUBJECT:\s*(.*)/);
  const bodyMatch = block.match(/BODY:\s*([\s\S]*)/);

  const draft = {
    to: toMatch ? toMatch[1].trim() : '',
    subject: subjectMatch ? subjectMatch[1].trim() : '',
    body: bodyMatch ? bodyMatch[1].trim() : '',
  };

  return { before, draft, after };
}

function parseCalendarAdd(text) {
  const match = text.match(/\[CALENDAR_ADD\]([\s\S]*?)\[\/CALENDAR_ADD\]/);
  if (!match) return { cleanText: text, calendarEvent: null };
  const cleanText = (text.slice(0, match.index) + text.slice(match.index + match[0].length)).trim();
  try {
    const calendarEvent = JSON.parse(match[1].trim());
    return { cleanText, calendarEvent };
  } catch {
    return { cleanText, calendarEvent: null };
  }
}

function parseCsvData(text) {
  const match = text.match(/\[CSV_DATA\]([\s\S]*?)\[\/CSV_DATA\]/);
  if (!match) return { cleanText: text, csvData: null };
  const cleanText = (text.slice(0, match.index) + text.slice(match.index + match[0].length)).trim();
  const block = match[1].trim();
  const filenameMatch = block.match(/^filename:\s*(.+)$/m);
  const filename = filenameMatch ? filenameMatch[1].trim() : 'export.csv';
  const csvContent = block.replace(/^filename:.+$/m, '').trim();
  return { cleanText, csvData: { filename, content: csvContent } };
}

function CsvDownloadCard({ csvData }) {
  const handleDownload = () => {
    const blob = new Blob([csvData.content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = csvData.filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  const rowCount = csvData.content.split('\n').filter(Boolean).length - 1;
  return (
    <div className="email-draft-card" style={{marginTop: '10px'}}>
      <div className="email-draft-header">
        <span className="email-draft-badge" style={{background:'rgba(22,101,52,0.08)',border:'1px solid rgba(22,101,52,0.18)',color:'#166534'}}>CSV EXPORT READY</span>
      </div>
      <div style={{fontSize:'13px',color:'var(--ink-muted)'}}>
        📄 {csvData.filename} — {rowCount} row{rowCount !== 1 ? 's' : ''}
      </div>
      <button className="email-draft-send-btn" onClick={handleDownload}>
        ⬇ Download CSV
      </button>
    </div>
  );
}

function CalendarAddCard({ event, onDone }) {
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleAdd = async () => {
    setStatus('adding');
    try {
      const res = await fetch('/.netlify/functions/calendar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', ...event }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to add event');
      setStatus('done');
      if (onDone) onDone(data.message);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  const startStr = event.start ? new Date(event.start).toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: event.allDay ? undefined : '2-digit',
    minute: event.allDay ? undefined : '2-digit',
  }) : '';

  return (
    <div className="email-draft-card">
      <div className="email-draft-header">
        <span className="email-draft-badge" style={{background:'rgba(91,78,248,0.10)',border:'1px solid rgba(91,78,248,0.22)',color:'var(--accent-1)'}}>ADD TO CALENDAR</span>
      </div>
      <div style={{fontSize:'15px',fontWeight:500}}>{event.summary}</div>
      <div style={{fontSize:'13px',color:'var(--ink-muted)'}}>{startStr}{event.location ? ` — ${event.location}` : ''}</div>
      {status === 'done' ? (
        <div className="email-draft-sent">✓ Added to calendar</div>
      ) : (
        <button className="email-draft-send-btn" onClick={handleAdd} disabled={status === 'adding'}>
          {status === 'adding' ? 'Adding…' : 'Add to Calendar'}
        </button>
      )}
      {status === 'error' && <div className="email-draft-error">Failed: {errorMsg}</div>}
    </div>
  );
}

function EmailDraftCard({ draft }) {
  const [to, setTo] = useState(draft.to);
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleApproveSend = async () => {
    setStatus('sending');
    setErrorMsg('');
    try {
      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject, body }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  return (
    <div className="email-draft-card">
      <div className="email-draft-header">
        <span className="email-draft-badge">DRAFT — not sent</span>
      </div>

      <label className="email-draft-field">
        <span>To</span>
        <input
          type="text"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          disabled={status === 'sending' || status === 'sent'}
        />
      </label>

      <label className="email-draft-field">
        <span>Subject</span>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={status === 'sending' || status === 'sent'}
        />
      </label>

      <label className="email-draft-field">
        <span>Body</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={status === 'sending' || status === 'sent'}
          rows={6}
        />
      </label>

      {status === 'sent' ? (
        <div className="email-draft-sent">✓ Sent</div>
      ) : (
        <button
          className="email-draft-send-btn"
          onClick={handleApproveSend}
          disabled={status === 'sending' || !to.trim() || !subject.trim()}
        >
          {status === 'sending' ? 'Sending…' : 'Approve & Send'}
        </button>
      )}

      {status === 'error' && <div className="email-draft-error">Failed to send: {errorMsg}</div>}
    </div>
  );
}

// ── Notes Panel ──────────────────────────────────────────────────────────────

function NotesPanel({ notes, onDone }) {
  const [open, setOpen] = useState(false);
  if (!notes || notes.length === 0) return null;

  const today = new Date().toISOString().slice(0, 10);
  const overdue = notes.filter(n => n.due && n.due < today);
  const dueToday = notes.filter(n => n.due === today);
  const upcoming = notes.filter(n => !n.due || n.due > today);

  return (
    <div className="notes-panel">
      <button className="notes-toggle" onClick={() => setOpen(o => !o)}>
        <span className="notes-toggle-icon">📋</span>
        <span className="notes-toggle-label">
          {notes.length} reminder{notes.length !== 1 ? 's' : ''}
          {(overdue.length + dueToday.length) > 0 && (
            <span className="notes-badge">{overdue.length + dueToday.length} due</span>
          )}
        </span>
        <span className="notes-toggle-arrow">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="notes-list">
          {overdue.length > 0 && (
            <div className="notes-group">
              <div className="notes-group-label overdue">Overdue</div>
              {overdue.map(n => <NoteItem key={n.id} note={n} onDone={onDone} />)}
            </div>
          )}
          {dueToday.length > 0 && (
            <div className="notes-group">
              <div className="notes-group-label today">Today</div>
              {dueToday.map(n => <NoteItem key={n.id} note={n} onDone={onDone} />)}
            </div>
          )}
          {upcoming.length > 0 && (
            <div className="notes-group">
              <div className="notes-group-label">Upcoming</div>
              {upcoming.map(n => <NoteItem key={n.id} note={n} onDone={onDone} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NoteItem({ note, onDone }) {
  const [done, setDone] = useState(false);
  const handleDone = async () => {
    setDone(true);
    await fetch('/.netlify/functions/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'remove', text: note.text }),
    });
    onDone();
  };
  const dueStr = note.due ? new Date(note.due + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : null;
  return (
    <div className={`note-item ${done ? 'done' : ''}`}>
      <button className="note-done-btn" onClick={handleDone} disabled={done} aria-label="Mark done">
        {done ? '✓' : '○'}
      </button>
      <div className="note-text-wrap">
        <span className="note-text">{note.text}</span>
        {dueStr && <span className="note-due">{dueStr}</span>}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────────

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memory, setMemory] = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load memory once authenticated
  useEffect(() => {
    if (authenticated) {
      loadMemory().then(m => {
        if (m) {
          setMemory(m);
          if (m.history) setHistory(m.history);
        }
      });
      loadNotes().then(n => setNotes(n));
    }
  }, [authenticated]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 160)}px`;
    }
  }, [input]);

  if (!authenticated) {
    return <LoginScreen onAuthenticated={() => setAuthenticated(true)} />;
  }

  const sendVoiceMessage = async (text) => {
    await sendMessageWithText(text);
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if ((!trimmed && !attachedFile) || isLoading) return;
    setInput('');
    const file = attachedFile;
    setAttachedFile(null);
    await sendMessageWithText(trimmed || 'Please analyse this file.', file);
  };

  const sendMessageWithText = async (trimmed, file = null) => {
    if (!trimmed || isLoading) return;

    const userMessage = file
      ? { role: 'user', content: [
          { type: 'text', text: trimmed || 'Please analyse this file.' },
          { type: 'document', source: { type: 'base64', media_type: file.mimeType, data: file.data } }
        ], displayContent: `📎 ${file.name}${trimmed ? ': ' + trimmed : ''}` }
      : { role: 'user', content: trimmed };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setError(null);
    setIsLoading(true);

    let emailContext = null;
    let weatherContext = null;
    let calendarContext = null;
    let contactsContext = null;

    if (mentionsEmail(trimmed)) {
      try {
        // Extract search intent from the user's message
        const emailPayload = { limit: 10, unreadOnly: true };

        // If user asks for all emails, disable unread filter
        if (/all (my )?(recent |latest )?emails|all emails|every email/i.test(trimmed)) {
          emailPayload.unreadOnly = false;
        }

        // Check for search keywords
        const searchMatch = trimmed.match(/(?:search|find|look for|looking for)\s+(?:emails?|mail)?\s*(?:about|from|with|for|containing)?\s+['"]?([^'"'.?!]{2,40})['"]?/i)
          || trimmed.match(/emails?\s+(?:about|from|with|containing)\s+['"]?([^'"'.?!]{2,40})['"]?/i);
        if (searchMatch) {
          emailPayload.search = searchMatch[1].trim();
          emailPayload.limit = 20;
        }

        // Check for specific folder
        const folderMatch = trimmed.match(/(?:in|from|check)\s+(?:my\s+)?([a-zA-Z0-9 _-]+)\s+(?:folder|mailbox)/i)
          || trimmed.match(/([a-zA-Z0-9 _-]+)\s+folder/i);
        if (folderMatch) {
          const folderName = folderMatch[1].trim();
          if (!['my', 'the', 'check', 'search'].includes(folderName.toLowerCase())) {
            emailPayload.folder = folderName;
          }
        }

        // Check for "all folders" intent
        if (/all folders|everywhere|all mailbox/i.test(trimmed)) {
          emailPayload.folder = 'ALL';
        }

        const emailResponse = await fetch('/.netlify/functions/email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailPayload),
        });
        if (emailResponse.ok) {
          const emailData = await emailResponse.json();
          emailContext = formatEmailsForPrompt(emailData.emails || []);
        } else {
          const errText = await emailResponse.text();
          emailContext = `<emails>Failed to fetch email: ${errText}</emails>`;
        }
      } catch (err) {
        emailContext = `<emails>Failed to fetch email: ${err.message}</emails>`;
      }
    }

    if (mentionsWeather(trimmed)) {
      weatherContext = await fetchWeatherContext(trimmed);
    }

    if (mentionsCalendar(trimmed)) {
      calendarContext = await fetchCalendarContext(trimmed);
    }

    if (mentionsContacts(trimmed, messages)) {
      contactsContext = await fetchContactsContext(trimmed);
    }

    const contextBlocks = [emailContext, weatherContext, calendarContext, contactsContext].filter(Boolean).join('\n\n');

    try {
      const response = await fetch('/.netlify/functions/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: nextMessages,
          emailContext: contextBlocks || null,
          memoryContext: formatMemoryContext(memory),
          historyContext: formatHistoryContext(history),
          notesContext: formatNotesContext(notes),
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      const data = await response.json();
      const rawText = data?.content?.[0]?.text || 'No response received.';

      const { cleanText: afterMemoryRaw, memoryUpdate } = parseMemoryUpdate(rawText);
      let afterMemory = afterMemoryRaw;

      if (memoryUpdate) {
        saveMemory(memoryUpdate).then(() => loadMemory().then(m => { if (m) setMemory(m); }));
      }

      // Handle note actions BEFORE setting messages so block is stripped from display
      const noteAction = parseNoteAction(afterMemory);
      if (noteAction) {
        if (noteAction.action === 'add') {
          addNote(noteAction.data.text, noteAction.data.due).then(() => loadNotes().then(n => setNotes(n)));
        } else if (noteAction.action === 'remove') {
          removeNote(noteAction.data.text).then(() => loadNotes().then(n => setNotes(n)));
        }
        afterMemory = afterMemory.replace(noteAction.raw, '').trim();
      }

      setMessages([...nextMessages, { role: 'assistant', content: afterMemory }]);

      // Save this exchange to conversation history
      const newTurns = [
        { role: 'user', content: trimmed },
        { role: 'assistant', content: afterMemory.replace(/\[CALENDAR_ADD\][\s\S]*?\[\/CALENDAR_ADD\]/g, '').replace(/\[MEMORY_UPDATE\][\s\S]*?\[\/MEMORY_UPDATE\]/g, '').replace(/\[CSV_DATA\][\s\S]*?\[\/CSV_DATA\]/g, '').trim() },
      ];
      saveConversationTurns(newTurns);
      setHistory(prev => [...prev, ...newTurns].slice(-40));
    } catch (err) {
      setError('Something went wrong reaching the assistant. Check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const startVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Voice input isn't supported in this browser. Try Safari on iPhone or Chrome on desktop.");
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;
    let finalTranscript = '';
    recognition.onstart = () => { setIsListening(true); finalTranscript = ''; };
    recognition.onend = () => {
      setIsListening(false);
      if (finalTranscript.trim()) {
        // Auto-send the voice message directly
        const transcript = finalTranscript.trim();
        setInput(transcript);
        // Use a small delay to let state update then trigger send
        setTimeout(() => {
          setInput('');
          sendVoiceMessage(transcript);
        }, 50);
      }
    };
    recognition.onerror = (e) => { setIsListening(false); console.warn('Speech error:', e.error); };
    recognition.onresult = (e) => {
      finalTranscript = '';
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalTranscript += e.results[i][0].transcript;
      }
    };
    try { recognition.start(); } catch (err) { setIsListening(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('File too large — max 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setAttachedFile({
        name: file.name,
        type: file.type,
        data: reader.result.split(',')[1], // base64
        mimeType: file.type,
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeAttachment = () => setAttachedFile(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="app">
      <header className="header">
        <NovaAvatar size={36} />
        <span className="header-title">Nova</span>
      </header>
      <NotesPanel notes={notes} onDone={() => loadNotes().then(n => setNotes(n))} />

      <main className="thread" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="empty-state">
            <p>Ask Nova anything to begin.</p>
          </div>
        )}

        {messages.map((msg, idx) => {
          return (
            <div key={idx} className={`message ${msg.role}`}>
              <div className="message-label">
                {msg.role === 'assistant' && <NovaAvatar size={18} />}
                {msg.role === 'user' ? 'You' : 'Nova'}
              </div>
              {(() => {
                if (msg.role !== 'assistant') {
                  return <div className="message-content">{msg.displayContent || (typeof msg.content === 'string' ? msg.content : '[file attachment]')}</div>;
                }
                const msgText = msg.displayContent || (typeof msg.content === 'string' ? msg.content : null);
                const emailParsed = msgText ? parseEmailDraft(msgText) : { draft: null, before: msgText, after: '' };
                if (emailParsed.draft) {
                  return (
                    <div className="message-content">
                      {emailParsed.before && <div className="draft-surrounding-text"><ReactMarkdown remarkPlugins={[remarkGfm]}>{emailParsed.before}</ReactMarkdown></div>}
                      <EmailDraftCard draft={emailParsed.draft} />
                      {emailParsed.after && <div className="draft-surrounding-text"><ReactMarkdown remarkPlugins={[remarkGfm]}>{emailParsed.after}</ReactMarkdown></div>}
                    </div>
                  );
                }
                const calParsed = msgText ? parseCalendarAdd(msgText) : { calendarEvent: null, cleanText: msgText };
                if (calParsed.calendarEvent) {
                  return (
                    <div className="message-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{calParsed.cleanText}</ReactMarkdown>
                      <CalendarAddCard event={calParsed.calendarEvent} />
                    </div>
                  );
                }
                const csvParsed = msgText ? parseCsvData(msgText) : { csvData: null, cleanText: msgText };
                if (csvParsed.csvData) {
                  return (
                    <div className="message-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{csvParsed.cleanText}</ReactMarkdown>
                      <CsvDownloadCard csvData={csvParsed.csvData} />
                    </div>
                  );
                }
                return (
                  <div className="message-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{typeof msg.content === 'string' ? msg.content : msgText || ''}</ReactMarkdown>
                  </div>
                );
              })()}
            </div>
          );
        })}

        {isLoading && (
          <div className="message assistant">
            <div className="message-label">
              <NovaAvatar size={18} />
              Nova
            </div>
            <div className="message-content thinking">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
            </div>
          </div>
        )}

        {error && <div className="error-banner">{error}</div>}
      </main>

      {attachedFile && (
        <div className="attachment-preview">
          <span className="attachment-name">📎 {attachedFile.name}</span>
          <button className="attachment-remove" onClick={removeAttachment}>✕</button>
        </div>
      )}
      <footer className="composer">
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message Nova..."
          rows={1}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt,.csv,.png,.jpg,.jpeg"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="mic-btn"
          aria-label="Attach file"
          type="button"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
          </svg>
        </button>
        <button
          onClick={startVoice}
          className={isListening ? 'mic-btn listening' : 'mic-btn'}
          aria-label="Voice input"
          type="button"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect x="9" y="2" width="6" height="11" rx="3" fill="currentColor"/>
            <path d="M5 10a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="9" y1="21" x2="15" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <button
          onClick={sendMessage}
          disabled={isLoading || !input.trim()}
          aria-label="Send message"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </footer>
    </div>
  );
}

export default App;
